import axios from "axios";

import { reportDeviceSession } from "../device/report-device-session";
import {
  cardUsageReadiness,
  provisionCard,
  tokenizeCvv,
} from "../services/api";
import {
  forgetFreshCvv,
  peekFreshCvv,
  rememberFreshCvv,
} from "./fresh-cvv-alias";
import { registerFundingCard } from "./register-funding-card";
import { isVgsConfigUnavailable } from "./tokenization-config";
import type {
  FundingInstrument,
  FundingInstrumentNew,
  FundingInstrumentSaved,
} from "../interfaces/funding";

export type PrepareFundingRailStatus = "ready" | "needs_cvv" | "failed";

export interface PrepareFundingRailResult {
  status: PrepareFundingRailStatus;
  funding: FundingInstrumentSaved;
  errorCode?:
    | "CVV_REQUIRED"
    | "PROVISION_FAILED"
    | "REGISTER_FAILED"
    | "READINESS_FAILED"
    | "CARD_CLASS_UNKNOWN"
    | "CARD_NOT_DEBIT"
    | "CARD_NOT_CREDIT"
    | "CARD_PULL_NOT_SUPPORTED"
    | "CARD_RAIL_UNAVAILABLE"
    | "VGS_CONFIG_UNAVAILABLE"
    | "GENERIC";
  /** Set with CARD_RAIL_UNAVAILABLE. True when the failure may clear later. */
  retryLater?: boolean;
  lastError?: string;
}

const isCvsDisabled = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false;
  const code = error.response?.data?.error?.code as string | undefined;
  return code === "CVS_NOT_ENABLED" || code === "CVS_DISABLED";
};

const CARD_PAY_CODES = [
  "CARD_CLASS_UNKNOWN",
  "CARD_NOT_DEBIT",
  "CARD_NOT_CREDIT",
  "CARD_PULL_NOT_SUPPORTED",
] as const;

type CardPayCode = (typeof CARD_PAY_CODES)[number];

const readinessErrorCode = (
  error: unknown
): "READINESS_FAILED" | CardPayCode => {
  if (!axios.isAxiosError(error)) return "READINESS_FAILED";
  const code = error.response?.data?.error?.code as string | undefined;
  if (code && (CARD_PAY_CODES as readonly string[]).includes(code)) {
    return code as CardPayCode;
  }
  return "READINESS_FAILED";
};

const railUnavailable = (
  error: unknown
): { retryLater: boolean } | null => {
  if (!axios.isAxiosError(error)) return null;
  const body = error.response?.data?.error as
    | { code?: string; details?: { retryLater?: boolean } }
    | undefined;
  if (body?.code !== "CARD_RAIL_UNAVAILABLE") return null;
  return { retryLater: body.details?.retryLater === true };
};

const toSaved = (
  funding: FundingInstrument
): FundingInstrumentSaved | null => {
  if (funding.source === "saved") return funding;
  return null;
};

/** Drop one-shot CVV alias so callers never leave it in store after prepare. */
const withoutPendingCvv = (
  funding: FundingInstrumentSaved
): FundingInstrumentSaved => {
  if (!funding.pendingCvvToken) return funding;
  const { pendingCvvToken: _drop, ...rest } = funding;
  return rest;
};

export const prepareFundingRail = async (args: {
  linkId: string;
  funding: FundingInstrument;
  capabilityKey: string;
  cvv?: string;
  fundingMethod?: "DEBIT_CARD" | "CREDIT_CARD";
}): Promise<PrepareFundingRailResult> => {
  const { linkId, capabilityKey } = args;

  // Refresh device posture before usage-readiness / provision (non-blocking).
  await reportDeviceSession(linkId);
  let funding: FundingInstrumentSaved;
  let cvvToken: string | undefined;
  const freshCvv = args.cvv?.trim() || undefined;

  try {
    const alreadySaved = toSaved(args.funding);
    if (alreadySaved) {
      funding = alreadySaved;
      // Just-added card: reuse the volatile alias from alta (no second CVV UI).
      // The quote page often rebuilds the saved instrument without pendingCvvToken.
      if (!freshCvv) {
        cvvToken =
          alreadySaved.pendingCvvToken ||
          peekFreshCvv(alreadySaved.paymentMethodId);
      }
    } else {
      // Fallback: add-card normally registers before confirm. Still register
      // here if a `new` instrument reaches prepare (interrupted alta / stale store).
      const neu = args.funding as FundingInstrumentNew;
      funding = await registerFundingCard(linkId, neu);
      if (!freshCvv && neu.cvvToken) {
        cvvToken = neu.cvvToken;
        rememberFreshCvv(funding.paymentMethodId, neu.cvvToken);
      }
    }
  } catch (error) {
    if (isVgsConfigUnavailable(error)) throw error;
    throw Object.assign(new Error("REGISTER_FAILED"), {
      code: "REGISTER_FAILED" as const,
    });
  }

  const clearPending = (): FundingInstrumentSaved => withoutPendingCvv(funding);

  const releaseStoredAlias = (): void => {
    forgetFreshCvv(funding.paymentMethodId);
  };

  let readiness;
  try {
    readiness = await cardUsageReadiness(linkId, funding.paymentMethodId, {
      capabilityKey,
      ...(args.fundingMethod ? { fundingMethod: args.fundingMethod } : {}),
    });
  } catch (error) {
    if (isCvsDisabled(error)) {
      return { status: "ready", funding: clearPending() };
    }
    const unavailable = railUnavailable(error);
    if (unavailable) {
      return {
        status: "failed",
        funding: clearPending(),
        errorCode: "CARD_RAIL_UNAVAILABLE",
        retryLater: unavailable.retryLater,
      };
    }
    return {
      status: "failed",
      funding: clearPending(),
      errorCode: readinessErrorCode(error),
    };
  }

  if (readiness.ready) {
    return { status: "ready", funding: clearPending() };
  }

  const cvvRequired = readiness.requirements?.cvvRequired === true;

  if (freshCvv) {
    try {
      cvvToken = await tokenizeCvv(freshCvv);
    } catch (error) {
      return {
        status: "needs_cvv",
        funding: clearPending(),
        errorCode: isVgsConfigUnavailable(error)
          ? "VGS_CONFIG_UNAVAILABLE"
          : "GENERIC",
      };
    }
  }

  if (cvvRequired && !cvvToken) {
    return {
      status: "needs_cvv",
      funding: clearPending(),
      errorCode: "CVV_REQUIRED",
    };
  }

  try {
    const provisioned = await provisionCard(linkId, funding.paymentMethodId, {
      capabilityKey,
      ...(cvvToken ? { cvvToken } : {}),
    });

    if (provisioned.ready) {
      releaseStoredAlias();
      return { status: "ready", funding: clearPending() };
    }

    releaseStoredAlias();
    return {
      status: "failed",
      funding: clearPending(),
      errorCode: "PROVISION_FAILED",
      lastError: provisioned.lastError,
    };
  } catch (error) {
    if (isCvsDisabled(error)) {
      return { status: "ready", funding: clearPending() };
    }

    if (axios.isAxiosError(error)) {
      const code = error.response?.data?.error?.code as string | undefined;
      if (code === "CVV_REQUIRED") {
        releaseStoredAlias();
        return {
          status: "needs_cvv",
          funding: clearPending(),
          errorCode: "CVV_REQUIRED",
        };
      }
    }

    const unavailable = railUnavailable(error);
    if (unavailable) {
      return {
        status: "failed",
        funding: clearPending(),
        errorCode: "CARD_RAIL_UNAVAILABLE",
        retryLater: unavailable.retryLater,
      };
    }

    return {
      status: "failed",
      funding: clearPending(),
      errorCode: "PROVISION_FAILED",
    };
  }
};
